import React, { useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  FooterAction,
  Icons,
} from '@ohif/ui-next';
import { ORGAN_DISPLAY_NAMES, OrganEnum, OrganQualityEnum } from '../../clients/viewer';
import { OrganRow, OrganRowState } from '../../services/OrganReviewService';
import { ANCHOR } from '../dialogAnchor';
import QualityToggle from './QualityToggle';

interface OrganReviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rows: readonly OrganRow[];
  isSubmitDisabled: boolean;
  isSubmitting: boolean;
  hasError: boolean;
  returnFocusRef: React.RefObject<HTMLButtonElement>;
  suppressFocusReturn: React.MutableRefObject<boolean>;
  onQualityChange: (organ: OrganEnum, quality?: OrganQualityEnum) => void;
  onSubmit: () => void;
}

const ROW_STATES: Record<OrganRowState, { disabled: boolean; note?: string }> = {
  unissued: { disabled: true, note: 'Not available for this series' },
  gradeable: { disabled: false },
  inProgress: { disabled: true, note: 'Submitting…' },
  complete: { disabled: true },
  failed: { disabled: false, note: 'Submission failed — try again' },
};

export default function OrganReviewDialog({
  open,
  onOpenChange,
  rows,
  isSubmitDisabled,
  isSubmitting,
  hasError,
  returnFocusRef,
  suppressFocusReturn,
  onQualityChange,
  onSubmit,
}: OrganReviewDialogProps) {
  const close = useCallback(() => onOpenChange(false), [onOpenChange]);

  const declineOpenFocus = useCallback((event: Event) => event.preventDefault(), []);

  const returnFocusToTrigger = useCallback(
    (event: Event) => {
      event.preventDefault();

      if (suppressFocusReturn.current) {
        suppressFocusReturn.current = false;
        return;
      }

      returnFocusRef.current?.focus();
    },
    [returnFocusRef, suppressFocusReturn]
  );

  const submitIfEnabled = useCallback(() => {
    if (!isSubmitDisabled) {
      onSubmit();
    }
  }, [isSubmitDisabled, onSubmit]);

  return (
    <Dialog
      open={open}
      modal={false}
      onOpenChange={onOpenChange}
      showOverlay={false}
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={false}
    >
      <DialogContent
        className="border-primary border sm:max-h-[300px] sm:max-w-[400px]"
        style={ANCHOR}
        onOpenAutoFocus={declineOpenFocus}
        onCloseAutoFocus={returnFocusToTrigger}
        data-cy="sycai-organ-review-dialog"
      >
        <DialogHeader>
          <DialogTitle>Organ Review</DialogTitle>
          <DialogDescription className="sr-only">
            Grade each organ Good or Poor, then submit.
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-[170px] flex-col gap-2 overflow-y-auto pt-2">
          <div className="grid grid-cols-2 gap-2 border-b px-1 pb-1 text-sm font-semibold text-white">
            <div>Organ</div>
            <div className="text-center">Quality</div>
          </div>

          {hasError && (
            <div
              className="text-destructive text-xs"
              data-cy="sycai-organ-review-error"
            >
              Could not read the organ reviews for this series. Showing the last known state.
            </div>
          )}

          {rows.map(row => {
            const { disabled, note } = ROW_STATES[row.state];

            return (
              <div
                key={row.organ}
                className="grid grid-cols-2 items-center gap-2 py-1"
                data-cy={`sycai-organ-row-${row.organ.toLowerCase()}`}
                data-sycai-row-state={row.state}
              >
                <div className="text-sm font-medium text-white">
                  {ORGAN_DISPLAY_NAMES[row.organ]}
                  {note && <div className="text-muted-foreground text-xs">{note}</div>}
                </div>

                <QualityToggle
                  organ={row.organ}
                  value={row.state === 'unissued' ? undefined : row.quality}
                  onChange={quality => onQualityChange(row.organ, quality)}
                  disabled={disabled}
                />
              </div>
            );
          })}
        </div>

        <FooterAction className="gap-3 pt-1 pb-2">
          <FooterAction.Secondary
            className="bg-primary/60 hover:bg-primary/100 flex-1 text-sm hover:text-white"
            dataCY="sycai-organ-review-close"
            onClick={close}
          >
            Save &amp; Close
          </FooterAction.Secondary>

          <FooterAction.Primary
            className="flex-1 bg-green-500 text-sm hover:bg-green-500/90 aria-disabled:opacity-50"
            dataCY="sycai-organ-review-submit"
            onClick={submitIfEnabled}
            aria-disabled={isSubmitDisabled || undefined}
          >
            {isSubmitting ? (
              <Icons.LoadingSpinner className="mr-1" />
            ) : (
              <Icons.StatusSuccess
                aria-hidden="true"
                className="mr-1 h-5 w-5"
              />
            )}
            Submit
          </FooterAction.Primary>
        </FooterAction>
      </DialogContent>
    </Dialog>
  );
}
